import {
  DASHBOARD,
  TEMPLATES,
  REPORTS,
  TIME_WISE_GRAPH_REPORTS,
  DAY_GRAPH_REPORT,
  TIME_WISE_SUMMARY_REPORTS,
  COMPANY_SETTINGS,
  DAY_SUMMARY_REPORT,
  SHIFT_SETTING,
  OPERATOR_SUMMARY_REPORT,
  OPERATOR_DETAIL_REPORT,
  PART_SUMMARY_REPORT,
  PART_DETAIL_REPORT,
  PERFORMANCE_REPORT,
  PART_SETTING,
  SETTING,
  STAFF_SETTING,
  BREAKDOWN,
  BREAKDOWN_SETTINGS,
  BREAKDOWN_REPORT,
  TIME_WISE_MULTIDAY_REPORTS,
  DAY_MULTIDAY_REPORTS,
  PRODUCTION_COMPONENT,
  NOTIFICATION_SETTINGS,
  BREAKDOWN_ANALYSIS,
  PNL_SUMMARY_REPORTS,
  COMPANY_TEMPLATES,
  USER_SETTINGS,
  PROFILE
} from "../constants/routes";

import get from "lodash/get"
import { Image, Tooltip } from "antd";
import logsIcon from "../static/icons/menu/logs.svg";
import leadsIcon from "../static/icons/menu/leads.svg";
import globeIcon from "../static/icons/menu/globe.svg";
import summaryIcon from "../static/icons/menu/summary.svg";
import reportIcon from "../static/icons/menu/report.svg";
import partIcon from "../static/icons/menu/part.svg";
import calendarIcon from "../static/icons/menu/calendar.svg";
import gridIcon from "../static/icons/menu/grid.svg";
import serverIcon from "../static/icons/menu/server.svg";
import settingIcon from "../static/icons/menu/setting.svg";
import clockIcon from "../static/icons/menu/clock.svg";
import operatorIcon from "../static/icons/menu/operator.svg";
import performanceIcon from "../static/icons/menu/performance.svg";
import partsIcon from "../static/icons/menu/parts.svg";
import breakdownIcon from "../static/icons/menu/fi_minus-square.svg";
import productionComponent from "../static/icons/menu/partitionOutlined.svg";
import broadCastIcon from "../static/icons/menu/Notificion.svg"
import antDesignAuditOutlined from "../static/icons/menu/AntDesignAuditOutlined.svg"
import pnlSummary from "../static/icons/menu/pnlsummary.svg"
import groupUsersIcon from "../static/icons/menu/groupUsers.svg"
import { iconstyle } from "../constants/stylesClasses";
import ImageContainer from "../components/ui/ImageContainer";
import { BREAKDOWN_PERMISSION, PNL_PERMISSION, NOTIFICATION_PERMISSION } from "../constants";

/**
 * What each role can see, based on the User Management capability matrix.
 * Roles are hierarchical: Super Admin > Admin > Manager > Supervisor > Operator,
 * so every "xOrAbove" flag includes all the roles above it.
 */
export const getMenuAccess = (roles = {}, permissions = undefined) => {
  const {
    isSuperAdmin,
    isOrganizationAdmin,
    isOrganizationManager,
    isOrganizationSupervisor,
    isOrganizationOperator,
  } = roles;

  const isAdminOrAbove = isSuperAdmin || isOrganizationAdmin;
  const isManagerOrAbove = isAdminOrAbove || isOrganizationManager;
  const isSupervisorOrAbove = isManagerOrAbove || isOrganizationSupervisor;
  const isOperatorOrAbove = isSupervisorOrAbove || isOrganizationOperator;

  const hasBreakdownPermission = !!get(permissions, BREAKDOWN_PERMISSION);
  const hasPnlPermission = !!get(permissions, PNL_PERMISSION);
  const hasNotificationPermission = !!get(permissions, NOTIFICATION_PERMISSION);

  return {
    // Dashboard
    dashboard: isOperatorOrAbove,

    // Super admin only
    companies: isSuperAdmin,
    allTemplates: isSuperAdmin,

    // Templates (update fields only)
    companyTemplates: isOrganizationAdmin,

    // Staff & portal users (can create lower-tier users)
    staff: isManagerOrAbove,
    users: isSupervisorOrAbove,

    // Settings
    shiftSettings: isOperatorOrAbove,
    productionComponents: isManagerOrAbove,
    parts: isManagerOrAbove,
    notificationSettings: isAdminOrAbove && hasNotificationPermission,

    // Breakdown — operators only get daily breakdown entry
    breakdownSettings: isOperatorOrAbove && hasBreakdownPermission,
    breakdownReports: isSupervisorOrAbove && hasBreakdownPermission,

    // Reports
    reports: isSupervisorOrAbove,
    pnlReport: isSupervisorOrAbove && hasPnlPermission,
  };
};

const menuIcon = ({ src, alt, isSelected, className = "" }) => (
  <ImageContainer>
    <Image
      preview={false}
      src={src}
      alt={alt}
      className={`${iconstyle} ${className} ${isSelected ? "white-color" : ""}`}
    />
  </ImageContainer>
);

const withTooltip = (text, title = text) => (
  <Tooltip placement="right" title={title}>
    {text}
  </Tooltip>
);

const buildMenuItem = (selectKey, {
  show = true,
  key,
  icon,
  alt = key,
  iconClassName,
  highlightKey = key,
  label,
  children,
}) => {
  if (!show) return null;

  const visibleChildren = children && children.filter(Boolean);
  if (children && visibleChildren.length === 0) return null;

  return {
    key,
    icon: menuIcon({
      src: icon,
      alt,
      className: iconClassName,
      isSelected: selectKey === highlightKey,
    }),
    label,
    ...(visibleChildren && { children: visibleChildren }),
  };
};

export const getAdminMenutems = (navigate, selectKey, roles, permissions = undefined) => {
  const can = getMenuAccess(roles, permissions);
  const item = (config) => buildMenuItem(selectKey, config);

  const menuItems = [
    item({
      show: can.dashboard,
      key: DASHBOARD,
      icon: gridIcon,
      alt: "dashboard",
      label: "Dashboard",
    }),

    item({
      show: can.companies,
      key: COMPANY_SETTINGS,
      icon: globeIcon,
      alt: "company settings",
      label: withTooltip("Company"),
    }),

    item({
      show: can.allTemplates,
      key: TEMPLATES,
      icon: serverIcon,
      alt: "templates",
      label: withTooltip("Templates", "Template"),
    }),

    item({
      show: can.companyTemplates,
      key: COMPANY_TEMPLATES,
      icon: serverIcon,
      alt: "company templates",
      label: withTooltip("Templates", "Template"),
    }),

    item({
      show: can.staff,
      key: STAFF_SETTING,
      icon: antDesignAuditOutlined,
      alt: "staff setting",
      label: withTooltip("Staff", "Staff Settings"),
    }),

    item({
      show: can.users,
      key: USER_SETTINGS,
      icon: groupUsersIcon,
      alt: "user setting",
      label: withTooltip("Users", "User Settings"),
    }),

    item({
      key: "SETTING",
      icon: settingIcon,
      alt: "setting",
      highlightKey: SETTING,
      label: "Settings",
      children: [
        item({
          show: can.shiftSettings,
          key: SHIFT_SETTING,
          icon: calendarIcon,
          alt: "shift setting",
          label: withTooltip("Shift Settings"),
        }),
        item({
          show: can.productionComponents,
          key: PRODUCTION_COMPONENT,
          icon: productionComponent,
          alt: "production",
          iconClassName: selectKey === PRODUCTION_COMPONENT ? "" : "opacity-80",
          label: withTooltip("Production Component"),
        }),
        item({
          show: can.parts,
          key: PART_SETTING,
          icon: partsIcon,
          alt: "part setting",
          label: withTooltip("Parts Settings"),
        }),
        item({
          show: can.notificationSettings,
          key: NOTIFICATION_SETTINGS,
          icon: broadCastIcon,
          alt: "notification",
          label: withTooltip("Notification Settings"),
        }),
      ],
    }),

    item({
      key: "BREAKDOWN",
      icon: breakdownIcon,
      alt: "breakdown",
      highlightKey: BREAKDOWN,
      label: "Breakdown",
      children: [
        item({
          show: can.breakdownSettings,
          key: BREAKDOWN_SETTINGS,
          icon: settingIcon,
          label: withTooltip("Breakdown Settings"),
        }),
        item({
          show: can.breakdownReports,
          key: BREAKDOWN_REPORT,
          icon: summaryIcon,
          alt: "breakdown report",
          label: withTooltip("Breakdown Report"),
        }),
        item({
          show: can.breakdownReports,
          key: BREAKDOWN_ANALYSIS,
          icon: reportIcon,
          label: withTooltip("Breakdown Analysis"),
        }),
      ],
    }),

    item({
      show: can.reports,
      key: "TIME_REPO",
      icon: clockIcon,
      alt: "time reports",
      highlightKey: REPORTS,
      label: "Time Reports",
      children: [
        item({
          key: TIME_WISE_GRAPH_REPORTS,
          icon: reportIcon,
          alt: "time graph",
          label: withTooltip("Graph", "Time Wise Reports"),
        }),
        item({
          key: TIME_WISE_SUMMARY_REPORTS,
          icon: summaryIcon,
          alt: "time summary",
          label: withTooltip("Summary", "Summary Reports"),
        }),
        item({
          key: TIME_WISE_MULTIDAY_REPORTS,
          icon: leadsIcon,
          alt: "day detail",
          label: withTooltip("Day Detail Report", "Summary Reports"),
        }),
        item({
          show: can.pnlReport,
          key: PNL_SUMMARY_REPORTS,
          icon: pnlSummary,
          alt: "pnl summary",
          label: withTooltip("PNL", "PNL Manager Wise Reports"),
        }),
      ],
    }),

    item({
      show: can.reports,
      key: "DAY_REPO",
      icon: calendarIcon,
      alt: "day reports",
      highlightKey: REPORTS,
      label: "Day Reports",
      children: [
        item({
          key: DAY_GRAPH_REPORT,
          icon: reportIcon,
          alt: "day graph",
          label: withTooltip("Graph", "Day Wise Reports"),
        }),
        item({
          key: DAY_SUMMARY_REPORT,
          icon: summaryIcon,
          alt: "day summary",
          label: withTooltip("Summary", "Day Wise Reports"),
        }),
        item({
          key: DAY_MULTIDAY_REPORTS,
          icon: leadsIcon,
          alt: "multi-day report",
          label: withTooltip("Multi-Day Report", "Day Wise Reports"),
        }),
      ],
    }),

    item({
      show: can.reports,
      key: "OPERATOR_REPO",
      icon: operatorIcon,
      alt: "operator reports",
      highlightKey: REPORTS,
      label: "Operator Reports",
      children: [
        item({
          key: OPERATOR_SUMMARY_REPORT,
          icon: logsIcon,
          alt: "operator summary",
          label: withTooltip("Summary", "Operator Summary Report"),
        }),
        item({
          key: OPERATOR_DETAIL_REPORT,
          icon: leadsIcon,
          alt: "operator detail",
          label: withTooltip("Detail", "Operator Detail Report"),
        }),
      ],
    }),

    item({
      show: can.reports,
      key: "PART_REPO",
      icon: partIcon,
      alt: "part reports",
      highlightKey: REPORTS,
      label: "Part Reports",
      children: [
        item({
          key: PART_SUMMARY_REPORT,
          icon: logsIcon,
          alt: "part summary",
          label: withTooltip("Summary", "Part Summary Report"),
        }),
        item({
          key: PART_DETAIL_REPORT,
          icon: leadsIcon,
          alt: "part detail",
          label: withTooltip("Detail", "Part Detail Report"),
        }),
      ],
    }),

    item({
      show: can.reports,
      key: PERFORMANCE_REPORT,
      icon: performanceIcon,
      alt: "performance report",
      label: "Performance Report",
    }),
  ];

  return menuItems.filter(Boolean);
};

export const getMenuItems = (onClickLogout,onClickLink) => [
  {
    key: "1",
    label: "Profile",
    onClick: () => onClickLink(PROFILE),
  },
  {
    key: "2",
    label: "Logout",
    onClick: () => onClickLogout(),
  },

];
